import { useEffect } from 'react';

export const SITE_URL = 'https://pixelutility.com';

/** Default social preview when page has no dedicated image */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/tools/resume-banner.webp`;

function upsertMeta(attribute, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function Seo({
  title,
  description,
  path = '',
  noIndex = false,
  image,
  imageAlt,
  imageWidth,
  imageHeight,
  keywords,
}) {
  const canonicalUrl = `${SITE_URL}${path}`;
  const pageTitle = title.includes('PixelUtility') ? title : `${title} | PixelUtility`;
  const imageUrl = image
    ? image.startsWith('http')
      ? image
      : `${SITE_URL}${image.startsWith('/') ? image : `/${image}`}`
    : undefined;

  const resolvedImage = imageUrl || (!path || path === '/' ? DEFAULT_OG_IMAGE : undefined);

  useEffect(() => {
    document.title = pageTitle;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertLink('canonical', canonicalUrl);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:locale', 'en_US');
    upsertMeta('property', 'og:title', pageTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:site_name', 'PixelUtility');
    if (resolvedImage) {
      upsertMeta('property', 'og:image', resolvedImage);
      upsertMeta('property', 'og:image:secure_url', resolvedImage);
      if (imageAlt) upsertMeta('property', 'og:image:alt', imageAlt);
      if (imageWidth) upsertMeta('property', 'og:image:width', String(imageWidth));
      if (imageHeight) upsertMeta('property', 'og:image:height', String(imageHeight));
    }
    upsertMeta('name', 'twitter:card', resolvedImage ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:title', pageTitle);
    upsertMeta('name', 'twitter:description', description);
    if (resolvedImage) {
      upsertMeta('name', 'twitter:image', resolvedImage);
      if (imageAlt) upsertMeta('name', 'twitter:image:alt', imageAlt);
    }
  }, [pageTitle, description, canonicalUrl, noIndex, resolvedImage, imageAlt, imageWidth, imageHeight, keywords]);

  return null;
}

export const SEO = {
  home: {
    title: 'PixelUtility | Free Online Tools – Resume, QR & Size Converter',
    description:
      'Free browser-based tools with full guides: ATS resume builder, static QR code generator, and US/UK/EU clothing & shoe size converter. Blog, FAQs, no sign-up.',
    path: '/',
    image: DEFAULT_OG_IMAGE,
    imageAlt:
      'PixelUtility free online tools — resume builder, QR code generator, and international size converter',
    imageWidth: 1024,
    imageHeight: 539,
  },
  about: {
    title: 'About PixelUtility | Free Browser-Based Tools',
    description:
      'PixelUtility builds free, privacy-friendly online tools: ATS resume builder, QR generator, and international size converter. Guides, blog, and live utilities.',
    path: '/about',
  },
  blog: {
    title: 'Blog | PixelUtility Guides – Resume, QR & Sizing',
    description:
      'Expert guides on ATS resumes, static vs dynamic QR codes, restaurant QR menus, US to EU shoe sizes, and how to measure feet accurately.',
    path: '/blog',
  },
  privacy: {
    title: 'Privacy Policy | PixelUtility',
    description:
      'Learn how PixelUtility collects and protects your data. Most tools run locally in your browser — your files are never uploaded to our servers.',
    path: '/privacy',
  },
  support: {
    title: 'Support PixelUtility | Help Keep Free Tools Online',
    description:
      'Support PixelUtility development with a coffee donation or get in touch at info@pixelutility.com. Help us build more free online productivity tools.',
    path: '/support',
  },
};
